import { useAuthContext } from '../../contexts/AuthContext';
import { useAccountPlan } from '../../hooks/useAccountUsage';
import { useFlag } from '../../hooks/useFeatureFlags';

/**
 * PlanBadge renders the account's current plan next to the account-role badge
 * in the navigation bar.
 *
 * Reads the plan from `GET /api/accounts/usage` (task 676 — previously the JWT
 * `plan` claim). When the account has no plan — e.g. a fresh signup that never
 * picked one — an attention-styled "No plan" badge renders instead of nothing
 * (task 466): the no-plan state must be ambiently visible, and the badge links
 * to the Billing page where a plan can be chosen.
 *
 * Nothing renders until the plan is actually known, so a slow or failed usage
 * request never flashes a false "No plan".
 */
export default function PlanBadge() {
  const { user } = useAuthContext();
  const { plan, isResolved } = useAccountPlan();
  // Task 994: the "No plan" state is one of the three ambient pick-a-plan
  // reminders. With the flag off the badge falls back to its pre-466
  // behaviour — nothing at all for a planless account. Called unconditionally
  // at the top, before any early return (React rules of hooks). A plan that is
  // actually present still renders its badge either way.
  const noticesEnabled = useFlag('innovate.fe.plan.no-plan-notices');

  // No user at all (unauthenticated render) — nothing to describe.
  if (!user) return null;

  // Plan not known yet — render nothing rather than a misleading "No plan".
  if (!isResolved) return null;

  if (!plan) {
    // Reminders switched off (task 994) — the same nothing the component
    // renders while the plan is not yet known.
    if (!noticesEnabled) return null;

    // Attention treatment: the account has no plan, so AI features are
    // disabled server-side. Plain anchor (not router Link) so the badge stays
    // free of router coupling — same reasoning as UpgradePrompt's links.
    return (
      <a
        href="/settings/subscription"
        data-testid="plan-badge"
        data-plan-state="none"
        className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-700 hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
        title="No plan — choose one on Settings → Subscription"
      >
        No plan
      </a>
    );
  }

  const planSlug = plan.toLowerCase();

  const styles: Record<string, string> = {
    free: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    paid: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300',
  };

  const className =
    styles[planSlug] ??
    'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300';

  // Capitalise the first letter for display
  const label = planSlug.charAt(0).toUpperCase() + planSlug.slice(1);

  return (
    <span
      data-testid="plan-badge"
      className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}
      title={`Plan: ${label}`}
    >
      {label}
    </span>
  );
}
